import type { FlatNode, TimeRange, TimeUnit } from './timelineUtils';
import { calculateBarPosition, ROW_HEIGHT } from './timelineUtils';
import GanttBar from './GanttBar';

interface DropTarget {
  issueId: string;
  valid: boolean;
}

interface Props {
  node: FlatNode;
  timeRange: TimeRange;
  timeUnit: TimeUnit;
  totalWidth: number;
  onIssueClick: (issueId: string) => void;
  onDateChange: (issueId: string, field: 'startDate' | 'dueDate', newDate: string) => void;
  onMoveStart: (issueId: string, issueType: string, e: React.MouseEvent) => void;
  dropTarget: DropTarget | null;
}

export default function GanttRow({
  node,
  timeRange,
  timeUnit,
  totalWidth,
  onIssueClick,
  onDateChange,
  onMoveStart,
  dropTarget,
}: Props) {
  const bar = calculateBarPosition(node.startDate, node.dueDate, timeRange, timeUnit);
  const isTarget = dropTarget?.issueId === node.issueId;
  const dropValid = isTarget && dropTarget!.valid;
  const dropInvalid = isTarget && !dropTarget!.valid;

  return (
    <div
      className={`relative border-b border-gray-100 ${
        dropValid ? 'ring-2 ring-inset ring-blue-400 bg-blue-50/50' : ''
      } ${dropInvalid ? 'ring-2 ring-inset ring-red-400 bg-red-50/50' : ''}`}
      style={{ height: `${ROW_HEIGHT}px`, minWidth: `${totalWidth}px` }}
      data-issue-id={node.issueId}
      data-issue-type={node.issueType}
    >
      {dropInvalid && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 text-sm z-10 pointer-events-none">
          &#128683;
        </span>
      )}
      {bar && (
        <GanttBar
          left={bar.left}
          width={bar.width}
          title={node.title}
          statusColor={node.workflowStatus.color}
          statusCategory={node.workflowStatus.category}
          issueId={node.issueId}
          issueType={node.issueType}
          startDate={node.startDate}
          dueDate={node.dueDate}
          timeUnit={timeUnit}
          onClick={() => onIssueClick(node.issueId)}
          onDateChange={onDateChange}
          onMoveStart={onMoveStart}
        />
      )}
    </div>
  );
}
